import { Component } from '../core/smpl-components.js'
import { SequenceScroller } from '../modules/sequence-scroller.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger.js'

gsap.registerPlugin(ScrollTrigger)

/**
 * AnimationScrollerSlides
 *
 * Maps AnimationScroller state to a slide index.
 * Supports threshold mode ([0, 0.3, 0.6]) and range mode ([[0], [150], [200]] or [[0, 80], [120, 180]]).
 * Listens to `animation-scroller:update` and calls `onIndexChange` when active index changes.
 *
 * AnimationScrollerSlides options:
 * mode: "frame" uses frame/targetFrame, "progress" uses 0..1 progress.
 * container: element that contains all slide elements (children = slides).
 * thresholds: slide mapping rules:
 *   - [0, 0.2, 0.4] = threshold steps
 *   - [[3, 20], [50, 70]] = explicit ranges
 *   - [[0], [150], [200]] = open-ended starts
 * onIndexChange(index, state): callback when active slide index changes.
 * useTargetFrame: true = use targetFrame, false = use currently drawn frame.
 * noMatch: "hold" keeps current slide if no rule matches, "none" returns -1 (hide all).
 */
class AnimationScrollerSlides {
	constructor(options = {}) {
		this.options = {
			mode: 'frame', // frame | progress
			container: null,
			thresholds: [], // [0, 0.2, 0.4] OR [[3, 20], [50, 70]]
			onIndexChange: null, // (index, state) => {}
			useTargetFrame: true, // use targetFrame instead of current frame
			noMatch: 'hold', // hold | none
			...options,
		}

		this.elContainer = this.options.container
		this.currentIndex = -1
		this.lastState = null
		this.boundHandleUpdate = this.handleUpdate.bind(this)

		this.slides = this.elContainer ? Array.from(this.elContainer.children) : []
	}

	connect(element) {
		this.element = element
		if (!this.element) return
		this.element.addEventListener('animation-scroller:update', this.boundHandleUpdate)
	}

	disconnect() {
		if (!this.element) return
		this.element.removeEventListener('animation-scroller:update', this.boundHandleUpdate)
		this.element = null
	}

	handleUpdate(event) {
		const state = event.detail
		this.lastState = state

		const nextIndex = this.getIndex(state)
		if (nextIndex === this.currentIndex) return

		this.gotoSlide(nextIndex)

		if (typeof this.options.onIndexChange === 'function') {
			this.options.onIndexChange(nextIndex, state)
		}
	}

	gotoSlide(index) {
		this.currentIndex = index

		this.slides.forEach((slide, i) => {
			const isActive = i === index
			gsap.to(slide, { opacity: isActive ? 1 : 0, duration: 0.5 })
		})
	}

	getIndex(state) {
		if (!state) return 0

		const config = Array.isArray(this.options.thresholds) ? this.options.thresholds : []
		if (!config.length) return 0

		const value = this.options.mode === 'progress' ? state.progress : this.options.useTargetFrame ? state.targetFrame : state.frame

		if (Array.isArray(config[0])) {
			const maxValue = this.options.mode === 'progress' ? 1 : state.frameCount - 1
			const normalized = []

			for (let i = 0; i < config.length; i += 1) {
				const item = config[i]
				if (!Array.isArray(item) || item.length < 1) continue

				const start = Number(item[0])
				if (Number.isNaN(start)) continue

				let end
				let derivedFromNext = false

				// Explicit [start, end]
				if (item.length >= 2) {
					end = Number(item[1])
					if (Number.isNaN(end)) continue
				} else {
					// Single [start] => up to next start (or maxValue if last)
					let nextStart = null
					for (let j = i + 1; j < config.length; j += 1) {
						const next = config[j]
						if (!Array.isArray(next) || next.length < 1) continue
						const n = Number(next[0])
						if (!Number.isNaN(n)) {
							nextStart = n
							break
						}
					}

					if (nextStart == null) {
						end = maxValue
					} else if (this.options.mode === 'progress') {
						end = nextStart // exclusive check below
					} else {
						end = nextStart - 1 // frame mode
					}
					derivedFromNext = true
				}

				// Normalize accidental reversed explicit ranges
				const min = Math.min(start, end)
				const max = Math.max(start, end)

				normalized.push({ min, max, derivedFromNext })
			}

			for (let i = 0; i < normalized.length; i += 1) {
				const r = normalized[i]

				// For derived progress ranges, use end-exclusive to avoid overlap.
				if (this.options.mode === 'progress' && r.derivedFromNext) {
					if (value >= r.min && value < r.max) return i
				} else {
					if (value >= r.min && value <= r.max) return i
				}
			}

			return this.options.noMatch === 'none' ? -1 : this.currentIndex
		}

		// Legacy threshold mode: [0, 0.2, 0.4]
		let index = 0
		for (let i = 0; i < config.length; i += 1) {
			const threshold = Number(config[i])
			if (Number.isNaN(threshold)) continue

			if (value >= threshold) {
				index = i
			} else {
				break
			}
		}

		return index
	}

	getCurrentIndex() {
		return this.currentIndex
	}

	destroy() {
		this.disconnect()
		this.lastState = null
		this.currentIndex = -1
	}
}

export default class extends Component {
	prepare() {
		this.animationConfig = JSON.parse(this.element.querySelector('script[data-animation-scroller-config]')?.textContent ?? '{}')

		// single line version of the above with optional chaining and nullish coalescing:

		this.animationScroller = new SequenceScroller({
			el: this.element,
			enableWebp: true,
			scrollDistanceVh: 400,
			preloadStrategy: 'spread',
			frameCount: parseInt(this.animationConfig.frames || 0),
			sets: this.animationConfig.sets || [],
		})

		// handle the slide index based on progress or frame changes
		this.animationScrollerSlides = new AnimationScrollerSlides({
			container: this.element.querySelector('.animation-scroller__slides'),
			mode: 'frame',
			thresholds: this.animationConfig.slideThresholds, // example ranges for 3 slides
			noMatch: 'none', // keep current slide if no range matches
			onIndexChange: (index, state) => {},
		})

		this.animationScrollerSlides.connect(this.element)
	}

	mount() {
		if (this.animationScroller) {
			this.animationScroller.init()
		}
	}

	unmount() {
		if (this.animationScroller) {
			this.animationScroller.destroy()
		}

		if (this.animationScrollerSlides) {
			this.animationScrollerSlides.destroy()
		}
	}
}
